import * as amplitude from "@amplitude/analytics-browser"
import { plugin as engagementPlugin } from "@amplitude/engagement-browser"
import type { WorkspaceUserRole } from "~/types/api/workspace"
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser"

export default defineNuxtPlugin(() => {
  const runtimeConfig = useRuntimeConfig()
  const disableAnalytics = runtimeConfig.public.preview
  const { session } = useAuth()

  if (!disableAnalytics) {
    amplitude.add(engagementPlugin({ serverZone: "EU" }))

    amplitude.init(
      runtimeConfig.public.amplitudeApiKey,
      session.value?.user.id,
      {
        serverZone: "EU",
        trackingOptions: {
          ipAddress: false,
        },
        autocapture: {
          attribution: false,
          pageViews: true,
          sessions: true,
          formInteractions: false,
          fileDownloads: false,
          elementInteractions: false,
          pageUrlEnrichment: true,
          webVitals: false,
        },
        deviceId: amplitude.getDeviceId(),
      },
    )
  }

  return {
    provide: {
      trackEvent: (...args: Parameters<typeof amplitude.track>) => {
        if (disableAnalytics) {
          return
        }
        amplitude.track(...args)
      },
      identifyUser: ({
        userId,
        workspaceId,
        userRole,
        isImpersonating,
      }: {
        userId?: string
        workspaceId?: string
        userRole?: WorkspaceUserRole
        isImpersonating: boolean
      }) => {
        if (disableAnalytics) {
          return
        }

        amplitude.setOptOut(isImpersonating || !workspaceId)

        if (userId) {
          const identifyObject = new amplitude.Identify()

          if (workspaceId) {
            identifyObject.set("workspace", workspaceId)
            amplitude.setGroup("customer", workspaceId)
          }

          if (userRole) {
            identifyObject.set("role", userRole)
          }

          amplitude.identify(identifyObject, { user_id: userId })

          const sessionReplayTracking = sessionReplayPlugin({
            sampleRate: 0.1,
          })
          amplitude.add(sessionReplayTracking)
        } else {
          amplitude.reset()
        }
      },
    },
  }
})
